import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { PenTool, RotateCcw, Check } from 'lucide-react';

const DigitalSignaturePad = ({ onSignatureChange, initialSignature, disabled = false }) => {
  const canvasRef = useRef(null);
  const signaturePadRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [isLibraryLoaded, setIsLibraryLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Dynamic import of signature_pad
  const loadSignaturePad = useCallback(async () => {
    try {
      const { default: SignaturePad } = await import('signature_pad');
      
      if (canvasRef.current && !signaturePadRef.current) {
        const canvas = canvasRef.current;
        
        // Set canvas size
        const rect = canvas.getBoundingClientRect();
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext('2d');
        ctx.scale(ratio, ratio);
        canvas.style.width = rect.width + 'px';
        canvas.style.height = rect.height + 'px';

        signaturePadRef.current = new SignaturePad(canvas, {
          backgroundColor: 'rgba(255, 255, 255, 0)',
          penColor: '#1e293b',
          velocityFilterWeight: 0.7,
          minWidth: 1,
          maxWidth: 3,
          throttle: 16,
          minDistance: 5,
        });

        // Handle signature changes
        const handleEndStroke = () => {
          if (signaturePadRef.current) {
            const signature = signaturePadRef.current.toDataURL();
            const isEmpty = signaturePadRef.current.isEmpty();
            setIsEmpty(isEmpty);
            if (onSignatureChange && !isEmpty) {
              onSignatureChange(signature);
            }
          }
        };

        const handleBeginStroke = () => {
          setIsEmpty(false);
        };

        signaturePadRef.current.addEventListener('endStroke', handleEndStroke);
        signaturePadRef.current.addEventListener('beginStroke', handleBeginStroke);
        
        setIsLibraryLoaded(true);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to load signature_pad library:', err);
      setError('Signatur-Bibliothek konnte nicht geladen werden');
    }
  }, [onSignatureChange]);

  useEffect(() => {
    loadSignaturePad();

    return () => {
      if (signaturePadRef.current) {
        try {
          signaturePadRef.current.off();
        } catch (err) {
          console.error('Error cleaning up signature pad:', err);
        }
      }
    };
  }, [loadSignaturePad]);

  useEffect(() => {
    if (initialSignature && signaturePadRef.current) {
      signaturePadRef.current.fromDataURL(initialSignature);
      setIsEmpty(false);
    }
  }, [initialSignature]);

  const clearSignature = () => {
    if (signaturePadRef.current) {
      signaturePadRef.current.clear();
      setIsEmpty(true);
      if (onSignatureChange) {
        onSignatureChange('');
      }
    }
  };

  const resizeCanvas = () => {
    if (canvasRef.current && signaturePadRef.current) {
      const canvas = canvasRef.current;
      const data = signaturePadRef.current.toData();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
      canvas.style.width = canvas.offsetWidth + 'px';
      canvas.style.height = canvas.offsetHeight + 'px';
      
      signaturePadRef.current.clear();
      signaturePadRef.current.fromData(data);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);

  if (error) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <PenTool className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="font-semibold text-red-900 mb-2">Signatur-Fehler</h3>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <PenTool className="w-5 h-5 text-emerald-600" />
          Digitale Signatur
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isLibraryLoaded && (
          <div className="flex items-center justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <span className="ml-2 text-slate-600">Lade Signatur-Komponente...</span>
          </div>
        )}
        
        <div className="relative">
          <canvas
            ref={canvasRef}
            className={`border-2 border-dashed border-slate-300 rounded-lg w-full h-40 ${
              disabled || !isLibraryLoaded ? 'cursor-not-allowed opacity-50' : 'cursor-crosshair'
            }`}
            disabled={disabled || !isLibraryLoaded}
          />
          {isEmpty && !disabled && isLibraryLoaded && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-slate-400 text-sm">Hier unterschreiben</p>
            </div>
          )}
          {!isLibraryLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-50 bg-opacity-75">
              <p className="text-slate-400 text-sm">Wird geladen...</p>
            </div>
          )}
        </div>
        
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={clearSignature}
            disabled={disabled || isEmpty || !isLibraryLoaded}
            className="flex-1 gap-2"
            data-testid="signature-clear-button"
          >
            <RotateCcw className="w-4 h-4" />
            Löschen
          </Button>
          {!isEmpty && isLibraryLoaded && (
            <Button
              type="button"
              variant="default"
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              disabled
            >
              <Check className="w-4 h-4" />
              Signiert
            </Button>
          )}
        </div>
        
        <div className="text-xs text-slate-500 text-center">
          {isLibraryLoaded 
            ? "Verwenden Sie Ihren Finger oder Stylus zum Signieren"
            : "Lade Signatur-Funktionalität..."
          }
        </div>
        
      </CardContent>
    </Card>
  );
};

export default DigitalSignaturePad;